/**
 * <store-locator>: the few things the Omnium Maps widget can't be told in Liquid.
 *
 * The app builds its markup in the browser, some of it only once a visitor
 * searches or filters, so everything here runs again whenever the widget
 * changes:
 *
 * - the app's own stylesheet is switched off, and the section styles the
 *   widget from scratch (see sections/store-locator.liquid);
 * - its labels, which the app only holds in one language, come from the
 *   theme's locale files as data attributes on this element;
 * - `data-lenis-prevent` on the list, so the wheel scrolls the list itself
 *   rather than the page (see assets/smooth-scroll.js);
 * - on narrow screens, where the map sits above the list, clicking a partner
 *   brings the map back into view with its marker open;
 * - the "Retour de consigne" filter. The app has no field for it: partners that
 *   take bottles back are the ones given their own marker image, so their
 *   cards and markers are tagged `data-returns` and the section's CSS hides the
 *   rest while the filter is on;
 * - the app's search only reacts to `keyup`, so any other change to the field
 *   (clearing it with its ×, pasting with the mouse, a phone's suggestions) is
 *   passed on to it as one.
 */
if (!customElements.get('store-locator')) {
  const APP_STYLES = 'omnium-maps-front-styles';

  /**
   * The app adds its stylesheet to <head> as it starts up, so it is switched
   * off either now or as soon as it turns up. `media`, not `disabled`: Chrome
   * can ignore `disabled` on a <link> that is still loading, and the app's
   * styles then come back.
   */
  function switchOff(link) {
    link.media = 'not all';
  }

  function dropAppStyles() {
    const link = document.getElementById(APP_STYLES);
    if (link) {
      switchOff(link);
      return;
    }

    const head = new MutationObserver(() => {
      const added = document.getElementById(APP_STYLES);
      if (!added) return;
      switchOff(added);
      head.disconnect();
    });
    head.observe(document.head, { childList: true });
  }

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;

      dropAppStyles();
      this.setUpReturnsFilter();
      this.apply();
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this, { childList: true, characterData: true, subtree: true });

      this.onClick = (event) => {
        if (!event.target.closest?.('.develic-heading')) return;
        if (!window.matchMedia('(max-width: 989px)').matches) return;

        const map = this.querySelector('.develic-right-panel');
        if (!map) return;

        const lenis = window.siroscope?.lenis;
        if (lenis) {
          lenis.scrollTo(map, { offset: -16 });
        } else {
          map.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      };
      this.addEventListener('click', this.onClick);

      // Typing sends a keyup of its own as well: the app just filters twice
      this.onInput = (event) => {
        if (!event.target.matches?.('.develic-search-input')) return;
        const list = this.querySelector('.develic-items-wrapper');
        if (list) list.scrollTop = 0;
        event.target.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
      };
      this.addEventListener('input', this.onInput);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      if (this.pending) cancelAnimationFrame(this.pending);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onInput);
    }

    /**
     * The list scrolls on its own, and with 142 partners that's easy to miss:
     * the arrow under it (and the fade above the arrow) show while there's
     * more below, and the arrow scrolls the list down.
     */
    watchScroll(list) {
      const update = () => {
        const end = list.scrollTop + list.clientHeight >= list.scrollHeight - 8;
        this.classList.toggle('is-list-end', end);
      };
      update();
      if (list === this.list) return;

      this.list = list;
      list.addEventListener('scroll', update, { passive: true });
      new ResizeObserver(update).observe(list);
      // The search hides cards with an inline style, which neither scrolls nor resizes the list
      new MutationObserver(update).observe(list, { attributes: true, attributeFilter: ['style'], subtree: true });
      this.querySelector('[data-locator-more]')?.addEventListener('click', () => {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        list.scrollBy({ top: list.clientHeight * 0.8, behavior: reduce ? 'auto' : 'smooth' });
      });
    }

    /**
     * The app numbers its cards (`data-id`) by their place in its data, and puts
     * a location's own marker image in an <img> inside that marker.
     */
    setUpReturnsFilter() {
      let items = [];
      try {
        items = JSON.parse(this.querySelector('[develic-map]')?.getAttribute('develic-map') || '{}').items || [];
      } catch {
        return;
      }

      this.returnIds = new Set();
      this.returnIcons = new Set();
      items.forEach((item, index) => {
        if (!item.marker_icon) return;
        this.returnIds.add(String(index));
        this.returnIcons.add(item.marker_icon);
      });
      if (!this.returnIds.size) return;

      const filters = this.querySelector('[data-locator-filters]');
      const toggle = filters?.querySelector('[data-locator-returns]');
      if (!toggle) return;
      filters.hidden = false;
      toggle.addEventListener('change', () => {
        this.classList.toggle('is-returns-only', toggle.checked);
        const list = this.querySelector('.develic-items-wrapper');
        if (list) list.scrollTop = 0;
        this.apply();
      });
    }

    /** The app adds its locations one at a time: wait for the batch to settle */
    schedule() {
      if (this.pending) return;
      this.pending = requestAnimationFrame(() => {
        this.pending = null;
        this.apply();
      });
    }

    apply() {
      // Changes made here are the observer's own doing, so stop listening first
      this.observer?.disconnect();

      this.querySelector('.develic-items-wrapper')?.setAttribute('data-lenis-prevent', '');

      if (this.returnIds?.size) {
        this.querySelectorAll('.develic-item[data-id]').forEach((card) => {
          card.toggleAttribute('data-returns', this.returnIds.has(card.dataset.id));
        });
        this.querySelectorAll('gmp-advanced-marker').forEach((marker) => {
          const own = Array.from(marker.querySelectorAll('img')).some((img) => this.returnIcons.has(img.getAttribute('src')));
          marker.toggleAttribute('data-returns', own);
        });
      }

      const search = this.querySelector('.develic-search-input');
      if (search && !search.dataset.labelled) {
        search.dataset.labelled = 'true';
        search.placeholder = this.dataset.searchPlaceholder || '';
        const label = search.id && this.querySelector(`label[for="${search.id}"]`);
        if (label && this.dataset.searchLabel) label.textContent = this.dataset.searchLabel;
      }

      if (this.dataset.directions) {
        this.querySelectorAll('.develic-direction:not([data-labelled])').forEach((link) => {
          link.dataset.labelled = 'true';
          link.textContent = this.dataset.directions;
        });
      }

      // Some addresses were pasted in with non-breaking spaces between every word,
      // which keeps them on one line and pushes them out of the card. Every pass
      // checks them all: the app re-renders a card's contents in place.
      const list = this.querySelector('.develic-items-wrapper');
      if (list) {
        const walker = document.createTreeWalker(list, NodeFilter.SHOW_TEXT);
        while (walker.nextNode()) {
          const text = walker.currentNode;
          if (text.data.includes(' ')) text.data = text.data.replace(/ /g, ' ');
        }
        this.watchScroll(list);
      }

      // The app appends its "nothing found" message after the "no locations" one
      const messages = Array.from(this.querySelectorAll('.develic-items-empty'));
      const noResults = messages.pop();
      if (noResults && this.dataset.emptySearch) noResults.textContent = this.dataset.emptySearch;
      if (this.dataset.empty) {
        messages.forEach((message) => {
          message.textContent = this.dataset.empty;
        });
      }

      this.observer?.observe(this, { childList: true, characterData: true, subtree: true });
    }
  }

  customElements.define('store-locator', StoreLocator);
}
